import { FileDownload, ModeEdit } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from "@mui/material";
import { Dayjs } from "dayjs";
import { ReactNode, useMemo } from "react";
import { CSVLink } from "react-csv";
import { downloadFile } from "../../Queries/CandidateQueries";
import { useAuthToken } from "../../State/Hooks/authHooks";
import { useUserProfileStore } from "../../State/Stores/UserProfileStore";
import { IEnhancedTableHeaderProps, ITablePagination } from "../../Typings/SortableTable";
import { ITableColumn } from "../../Typings/TableColumn";
import createSlug from "../../Utilities/createSlug";
import { formatPhone } from "../../Utilities/formatUtilities";
import ListSkeleton from "../Skeleton/ListSkeleton";
import { useSortableTableState } from "./State/SortableTableState";
import { CustomTableRow } from "./Subcomponents/CustomTableRow";
import { EnhancedTableHead } from "./Subcomponents/EnhancedTableHead";
import TableColumnSelection from "./Subcomponents/TableColumnSelection";

export default function SortableTable({
  columns = [],
  tableData = [],
  name,
  loading = false,
  isEditingGroup = false,
  selected = [],
  handleSelected = () => {
    return;
  },
  handleSelectAllClick = () => {
    return;
  },
  isRowSelected = () => false,
  pagination = {} as ITablePagination,
  emptyMessage = "No data available",
  rowTitle,
  exportFileName,
  exportEnabled = false,
  defaultOrderBy,
  columnEditEnabled = true,
  renderCustomCell,
  customOrder,
  customOrderBy,
  customRequestSort,
  getRowId,
  serverSideExport = false,
  serverSideExportUrlOrCallback,
  useServerSidePagination = false,
  isRowDisabled = () => false,
  disabledRowTooltip = "",
  enhancedTableHeaderProps,
  disableTruncation = false,
  hoverRowEffect = true,
  footerActions,
}: {
  columns: ITableColumn[];
  tableData: any[] | undefined;
  name: string;
  loading?: boolean;
  isEditingGroup?: boolean;
  selected?: any;
  handleSelected?: (row: any, select: boolean, e: any) => void;
  handleSelectAllClick?: (row: any) => void;
  isRowSelected?: (row: any) => boolean;
  pagination?: ITablePagination;
  emptyMessage?: string;
  rowTitle?: string | ((row: { [x: string]: string | number | any[] | Date }) => string);
  exportFileName?: string;
  exportEnabled?: boolean;
  defaultOrderBy?: string;
  columnEditEnabled?: boolean;
  renderCustomCell?: (
    row: any,
    column: ITableColumn,
    adjustDate: (date: Date | string | Dayjs) => Dayjs,
  ) => React.ReactNode;
  customOrder?: "asc" | "desc";
  customOrderBy?: string;
  customRequestSort?: (event: React.MouseEvent<unknown>, property: string) => void;
  getRowId?: (row: { [x: string]: string | number | any[] | Date }) => string;
  serverSideExport?: boolean;
  serverSideExportUrlOrCallback?: string | (() => void);
  useServerSidePagination?: boolean;
  isRowDisabled?: (row: any) => boolean;
  disabledRowTooltip?: string;
  enhancedTableHeaderProps?: Partial<IEnhancedTableHeaderProps>;
  disableTruncation?: boolean;
  hoverRowEffect?: boolean;
  footerActions?: ReactNode;
}) {
  const token = useAuthToken();
  const { profile } = useUserProfileStore();

  const {
    getters: {
      filteredData,
      rowsPerPage,
      page,
      order,
      orderBy,
      emptyRows,
      editTableColumnsOpen,
      viewableColumns,
    },
    handlers: { handleChangePage, handleChangeRowsPerPage, handleRequestSort, viewableCheckboxOnClick },
    effects: { getComparator, stableSort },
    setters: { setEditTableColumnsOpen },
  } = useSortableTableState(tableData, columns, name, loading, pagination, defaultOrderBy);

  const displayPage = pagination.enabled ? pagination.currentPage || page : page;
  const displayRowsPerPage = pagination.enabled ? pagination.rows || rowsPerPage : rowsPerPage;

  const displayOrder = customOrder || order;
  const displayOrderBy = customOrderBy || orderBy;

  const handleChangeCurrentPage = pagination.enabled
    ? pagination.handleChangePage || handleChangePage
    : handleChangePage;
  const handleChangeRows = pagination.enabled
    ? pagination.handleChangeRowsPerPage || handleChangeRowsPerPage
    : handleChangeRowsPerPage;

  const handleChangeSortBy = customRequestSort || handleRequestSort;

  const getCsvHeaders = () => {
    const headers = [] as any[];

    columns.forEach((c) => {
      headers.push({ label: c.label, key: c.propName });
    });

    return headers;
  };

  const formatDataForExport = () => {
    const exportData = [] as any[];

    tableData.forEach((c) => {
      const data = {} as any;
      Object.keys(c).forEach((k) => {
        if (k !== "actions") {
          if (k === "emailAddresses") {
            data[k] = c[k].map((e: { emailAddress: string }) => e.emailAddress).join(",");
          } else if (k === "phoneNumbers") {
            data[k] = c[k].map((e: { phoneNumber: string }) => formatPhone(e.phoneNumber)).join(",");
          } else if (k === "locations") {
            data[k] = c[k].map((e: { location: string }) => e.location).join(",");
          } else if (k === "tags") {
            data[k] = c[k].map((e: { tag: string }) => e.tag).join(",");
          } else if (k === "attachments") {
            data[k] = c[k].map((e: { fileUrl: string }) => e.fileUrl).join(",");
          } else {
            data[k] = c[k];
          }
        }
      });
      exportData.push(data);
    });

    return exportData;
  };

  const tableDisplayData = useMemo(() => {
    const baseData = stableSort(tableData, getComparator(displayOrder, displayOrderBy));

    return baseData.slice(
      useServerSidePagination ? 0 : displayPage * displayRowsPerPage,
      useServerSidePagination ? displayRowsPerPage : displayPage * displayRowsPerPage + displayRowsPerPage,
    );
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [tableData, displayOrder, useServerSidePagination, displayOrderBy, displayPage, displayRowsPerPage]);

  return (
    <>
      <Box sx={{ overflow: "auto" }}>
        <Box sx={{ width: "100%", display: "table", tableLayout: "fixed" }}>
          <TableContainer data-test={name}>
            {loading ? (
              <ListSkeleton
                numRows={
                  filteredData.length > 0 && filteredData.length < displayRowsPerPage
                    ? filteredData.length
                    : displayRowsPerPage
                }
              />
            ) : (
              <Table stickyHeader size="small" aria-label="sticky table">
                <EnhancedTableHead
                  numSelected={selected.length}
                  onRequestSort={handleChangeSortBy}
                  onSelectAllClick={handleSelectAllClick}
                  order={displayOrder}
                  orderBy={displayOrderBy}
                  rowCount={filteredData.filter((c) => !isRowDisabled(c)).length}
                  columns={viewableColumns ?? columns}
                  isEditing={isEditingGroup}
                  {...enhancedTableHeaderProps}
                />
                <TableBody>
                  {tableDisplayData.map((row, index) => {
                    const title = rowTitle
                      ? typeof rowTitle === "string"
                        ? rowTitle
                        : rowTitle(row)
                      : undefined;
                    const customId = getRowId ? getRowId(row) : undefined;
                    const key = customId
                      ? `${customId}-${index}`
                      : title
                        ? `${createSlug(title)}-${index}`
                        : index;
                    return (
                      <CustomTableRow
                        timezoneOffset={profile.daylightSavingOffset}
                        hover={hoverRowEffect}
                        rowDisabled={isRowDisabled(row)}
                        disabledRowTooltip={disabledRowTooltip}
                        key={key}
                        columns={viewableColumns ?? columns}
                        isEditing={isEditingGroup}
                        row={row}
                        handleRowClick={handleSelected}
                        rowChecked={isRowSelected?.(row)}
                        title={title}
                        renderCustomCell={renderCustomCell}
                        customId={customId}
                        disableTruncation={disableTruncation}
                      />
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow
                      style={{
                        height: 53 * emptyRows,
                      }}
                    >
                      <TableCell data-test="table-body-cell" colSpan={columns.length + 1} />
                    </TableRow>
                  )}
                  {tableData.length === 0 ? (
                    <TableRow>
                      <TableCell data-test="table-body-cell" colSpan={columns.length + 1}>
                        {emptyMessage}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          <Grid container sx={{ marginTop: "15px" }}>
            <Grid item xs={12} md={6} sx={{ marginTop: "10px" }}>
              {exportEnabled &&
                tableData &&
                tableData.length > 0 &&
                (serverSideExport ? (
                  <Button
                    title="Export Data"
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<FileDownload color="primary" />}
                    onClick={async () => {
                      if (
                        serverSideExportUrlOrCallback &&
                        typeof serverSideExportUrlOrCallback === "string"
                      ) {
                        await downloadFile(token, serverSideExportUrlOrCallback ?? "");
                      } else if (
                        serverSideExportUrlOrCallback &&
                        typeof serverSideExportUrlOrCallback === "function"
                      ) {
                        serverSideExportUrlOrCallback?.();
                      }
                    }}
                  >
                    Export
                  </Button>
                ) : (
                  <CSVLink
                    headers={getCsvHeaders()}
                    data={formatDataForExport()}
                    filename={`${exportFileName ?? "export"}-${new Date().toJSON().slice(0, 10)}`}
                  >
                    <Button
                      title="Export Data"
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<FileDownload color="primary" />}
                    >
                      Export
                    </Button>
                  </CSVLink>
                ))}
              {columnEditEnabled && (
                <Button
                  onClick={() => setEditTableColumnsOpen(true)}
                  size="small"
                  title="Edit Table Columns"
                  variant="outlined"
                  sx={{ marginLeft: "10px" }}
                  color="primary"
                  aria-label="edit columns button"
                  startIcon={<ModeEdit color="primary" />}
                >
                  Edit Columns
                </Button>
              )}
              {footerActions}
            </Grid>
            <Grid item xs={12} md={6}>
              {pagination.enabled && (
                <TablePagination
                  rowsPerPageOptions={pagination.rowsPerPage || [5, 10, 15]}
                  component="div"
                  count={pagination.totalCount ?? tableData.length}
                  rowsPerPage={displayRowsPerPage}
                  page={displayPage}
                  onPageChange={handleChangeCurrentPage}
                  onRowsPerPageChange={handleChangeRows}
                  data-test="tablePagination"
                  labelRowsPerPage={pagination.labelRowsPerPage}
                  labelDisplayedRows={pagination.labelDisplayedRows}
                />
              )}
            </Grid>
          </Grid>
          <TableColumnSelection
            columns={columns}
            handleModalClose={() => setEditTableColumnsOpen(false)}
            isOpen={editTableColumnsOpen}
            onCheckboxChange={viewableCheckboxOnClick}
            viewableColumns={viewableColumns ?? []}
          />
        </Box>
      </Box>
    </>
  );
}
